/**
 * MARK outbox: a mark is never lost to a bad signal. Every mark carries a `clientId`; if the POST
 * fails for a network reason it is kept on the phone (localStorage) and re-sent until the server
 * accepts it. The server de-duplicates on `clientId`, so a retry after a lost response is safe.
 */
import type { Id, Marker } from '@apos/shared';
import { ApiError, api, type MarkerInput } from '../../lib/api';

export interface QueuedMark {
  missionId: Id;
  input: MarkerInput & { clientId: string };
  queuedAt: number;
  attempts: number;
}

const KEY = 'apos.markOutbox.v1';
const MAX = 500;

export function newClientId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** A failure worth retrying later (offline, server down/overloaded) vs. a real refusal. */
export function isRetryable(e: unknown): boolean {
  if (e instanceof ApiError) return e.isNetwork || e.status >= 500 || e.status === 429 || e.status === 408;
  return true; // unknown fetch/abort errors
}

function read(): QueuedMark[] {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    const v = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(v) ? (v as QueuedMark[]) : [];
  } catch {
    return [];
  }
}

function write(list: QueuedMark[]): void {
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(list.slice(-MAX)));
  } catch {
    /* private mode / quota: the in-memory copy still retries this session */
  }
}

type Listener = (n: number) => void;

export class MarkOutbox {
  private mem: QueuedMark[] = read();
  private listeners = new Set<Listener>();
  private flushing = false;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly send: (missionId: Id, input: MarkerInput) => Promise<Marker> = (id, input) => api.markers.create(id, input),
    private readonly onSynced: (m: Marker) => void = () => undefined,
  ) {}

  get size(): number {
    return this.mem.length;
  }

  pending(missionId?: Id): QueuedMark[] {
    return missionId ? this.mem.filter((q) => q.missionId === missionId) : [...this.mem];
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private changed(): void {
    write(this.mem);
    for (const l of this.listeners) l(this.mem.length);
  }

  enqueue(missionId: Id, input: MarkerInput & { clientId: string }): void {
    if (this.mem.some((q) => q.input.clientId === input.clientId)) return;
    this.mem.push({ missionId, input, queuedAt: Date.now(), attempts: 0 });
    this.changed();
  }

  clear():void { this.mem=[];this.changed(); }

  /** Try to deliver everything queued. Returns how many were delivered. */
  async flush(): Promise<number> {
    if (this.flushing || this.mem.length === 0) return 0;
    this.flushing = true;
    let sent = 0;
    try {
      for (const q of [...this.mem]) {
        try {
          const m = await this.send(q.missionId, q.input);
          this.mem = this.mem.filter((x) => x.input.clientId !== q.input.clientId);
          sent++;
          this.onSynced(m);
        } catch (e) {
          q.attempts++;
          if (!isRetryable(e)) {
            // refused for good (mission deleted, not a member…): drop it rather than retry forever
            this.mem = this.mem.filter((x) => x.input.clientId !== q.input.clientId);
          } else break; // still offline — stop and wait for the next tick
        }
      }
    } finally {
      this.flushing = false;
      this.changed();
    }
    return sent;
  }

  /** Retry every `everyMs` and whenever the browser comes back online. */
  start(everyMs = 5000): void {
    if (this.timer) return;
    this.timer = setInterval(() => void this.flush(), everyMs);
    if (typeof window !== 'undefined') window.addEventListener('online', () => void this.flush());
    void this.flush();
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}
